// Programs.js
import React, { useState, useEffect, useMemo } from 'react';
import { FaArrowUp, FaArrowDown } from 'react-icons/fa';
import programsData from './programsData.json'; // Adjust path if needed

/*********************************
 * SORTING & HELPER FUNCTIONS
 *********************************/
function sortPrograms(arr, sortKey, ascending) {
  if (!sortKey) return arr;
  const sorted = [...arr].sort((a, b) => {
    let aVal, bVal;
    switch (sortKey) {
      case 'programCode':
        aVal = a.programCode.toLowerCase();
        bVal = b.programCode.toLowerCase();
        break;
      case 'programTitle':
        aVal = a.programTitle.toLowerCase();
        bVal = b.programTitle.toLowerCase();
        break;
      case 'yearsToDegree':
        aVal = a.yearsToDegree;
        bVal = b.yearsToDegree;
        break;
      case 'hasProgramMaps':
        aVal = a.hasProgramMaps ? 1 : 0;
        bVal = b.hasProgramMaps ? 1 : 0;
        break;
      case 'programMapsCount':
        aVal = a.programMapsCount || 0;
        bVal = b.programMapsCount || 0;
        break;
      case 'creditsToDegree':
        aVal = a.creditsToDegree;
        bVal = b.creditsToDegree;
        break;
      case 'creditRatio':
        aVal = a.creditRatio;
        bVal = b.creditRatio;
        break;
      default:
        aVal = '';
        bVal = '';
    }
    if (aVal < bVal) return ascending ? -1 : 1;
    if (aVal > bVal) return ascending ? 1 : -1;
    return 0;
  });
  return sorted;
}

function average(values) {
  if (!values.length) return 0;
  return values.reduce((sum, v) => sum + v, 0) / values.length;
}

/**
 * Returns a fill bar for "Years to Degree" with color-coded fill:
 * - < 4.0 => Green
 * - 4.0 - 4.9 => Orange
 * - >= 5.0 => Red
 */
function YearsToDegreeBar({ years }) {
  const maxYears = 6; // define how wide "100%" is
  const fillPercent = Math.min((years / maxYears) * 100, 100);

  let fillColor = 'bg-green-500';
  if (years >= 4 && years < 5) {
    fillColor = 'bg-orange-400';
  } else if (years >= 5) {
    fillColor = 'bg-red-500';
  }

  return (
    <div className="flex items-center gap-2">
      <span className="text-sm font-semibold">{years}</span>
      <div className="relative w-24 h-3 bg-gray-200 rounded-full">
        <div
          className={`absolute top-0 left-0 h-3 rounded-full ${fillColor}`}
          style={{ width: `${fillPercent}%` }}
        />
      </div>
    </div>
  );
}

/**
 * Returns ratio with an up arrow if ratio >= 80, otherwise down arrow.
 */
function CreditRatioCell({ ratio }) {
  const isUp = ratio >= 80;
  const arrowClasses = isUp
    ? 'bg-green-100 text-green-600'
    : 'bg-red-100 text-red-600';

  return (
    <div className="flex items-center gap-2">
      <span>{ratio}%</span>
      <div
        className={`relative w-4 h-4 rounded-full flex items-center justify-center ${arrowClasses}`}
      >
        {isUp ? (
          <FaArrowUp style={{ fontSize: '0.7rem' }} />
        ) : (
          <FaArrowDown style={{ fontSize: '0.7rem' }} />
        )}
      </div>
    </div>
  );
}

/*********************************
 * MAIN COMPONENT
 *********************************/
const Programs = ({ onSelectProgram }) => {
  const [programs, setPrograms] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, asc: true });

  // For search
  const [searchQuery, setSearchQuery] = useState('');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 7;

  // Load data on mount
  useEffect(() => {
    setPrograms(programsData);
  }, []);

  /*********************************
   * SORT + SEARCH + FILTER
   *********************************/
  const sortedPrograms = useMemo(() => {
    if (!sortConfig.key) return programs;
    return sortPrograms(programs, sortConfig.key, sortConfig.asc);
  }, [programs, sortConfig]);

  const filteredPrograms = useMemo(() => {
    if (!searchQuery) return sortedPrograms;
    const lowerSearch = searchQuery.toLowerCase();
    return sortedPrograms.filter(
      (p) =>
        p.programCode.toLowerCase().includes(lowerSearch) ||
        p.programTitle.toLowerCase().includes(lowerSearch)
    );
  }, [sortedPrograms, searchQuery]);

  // Pagination
  const totalRecords = filteredPrograms.length;
  const totalPages = Math.ceil(totalRecords / rowsPerPage);
  const startIndex = totalRecords === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
  const endIndex =
    totalRecords === 0
      ? 0
      : Math.min(startIndex + rowsPerPage - 1, totalRecords);

  const currentTableData = useMemo(() => {
    const startIdx = (currentPage - 1) * rowsPerPage;
    return filteredPrograms.slice(startIdx, startIdx + rowsPerPage);
  }, [filteredPrograms, currentPage]);

  /*********************************
   * EVENT HANDLERS
   *********************************/
  const handleSortColumn = (fieldKey) => {
    setSortConfig((prev) => {
      if (prev.key === fieldKey) {
        return { key: fieldKey, asc: !prev.asc };
      }
      return { key: fieldKey, asc: true };
    });
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) setCurrentPage((p) => p - 1);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage((p) => p + 1);
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1); // reset to first page on new search
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    // Real-time filtering is already handled in handleSearchChange
  };

  // Row click to show details
  const handleRowClick = (program) => {
    if (onSelectProgram) {
      onSelectProgram(program);
    }
  };

  /*********************************
   * COMPUTED STATS FOR CARDS
   *********************************/
  const avgYears = useMemo(
    () => average(programs.map((p) => p.yearsToDegree)).toFixed(1),
    [programs]
  );
  const avgCredits = useMemo(
    () => average(programs.map((p) => p.creditsToDegree)).toFixed(0),
    [programs]
  );
  const avgCreditRatio = useMemo(
    () => average(programs.map((p) => p.creditRatio)).toFixed(1),
    [programs]
  );
  // placeholders
  const overallCompletionRate = 87;
  const avgCrInProgramMaps = 104;

  /*********************************
   * RENDER
   *********************************/
  return (
    <div className="flex-1 p-4">
      <div className="p-4 bg-gray-50 shadow-md border rounded-lg min-h-[70vh]">
        {/* TITLE + SEARCH (one row) */}
        <div className="flex flex-nowrap items-center justify-between mb-4">
          <h5 className="text-xl font-bold text-gray-800 whitespace-nowrap">
            Programs Analysis
          </h5>

          {/* SEARCH BAR */}
          <div className="w-full max-w-md">
            <form onSubmit={handleSearchSubmit} className="flex items-center w-full">
              <div className="relative w-full">
                {/* Search Icon */}
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <svg
                    className="w-5 h-5 text-gray-500 dark:text-gray-400"
                    aria-hidden="true"
                    fill="none"
                    viewBox="0 0 20 20"
                  >
                    <path
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z"
                    />
                  </svg>
                </div>

                {/* Search Input */}
                <input
                  type="search"
                  id="default-search"
                  className="block w-full py-2 pl-10 pr-16 text-sm text-gray-900 
                             border border-gray-300 rounded-lg bg-gray-50
                             focus:ring-blue-500 focus:border-blue-500
                             dark:bg-gray-700 dark:border-gray-600
                             dark:placeholder-gray-400 dark:text-white
                             dark:focus:ring-blue-500 dark:focus:border-blue-500"
                  placeholder="Search Program Code or Title..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  required
                />
              </div>
            </form>
          </div>
        </div>

        {/* SUMMARY CARDS (span full width) */}
        <div className="grid grid-cols-5 gap-4 w-full mb-6">
          {/* Card 1 */}
          <div className="bg-white rounded-lg p-4 flex flex-col border border-gray-300">
            <span className="text-sm text-gray-500">Overall Completion Rate</span>
            <span className="text-2xl font-semibold text-gray-800">
              {overallCompletionRate}%
            </span>
            <div className="text-sm text-green-600 flex items-center gap-1">
              <FaArrowUp />
              <span>2% from last year</span>
            </div>
          </div>

          {/* Card 2 */}
          <div className="bg-white rounded-lg p-4 flex flex-col border border-gray-300">
            <span className="text-sm text-gray-500">Avg. Time to Completion</span>
            <span className="text-2xl font-semibold text-gray-800">
              {avgYears} years
            </span>
            <div className="text-sm text-green-600 flex items-center gap-1">
              <FaArrowUp />
              <span>3% from last year</span>
            </div>
          </div>

          {/* Card 3 */}
          <div className="bg-white rounded-lg p-4 flex flex-col border border-gray-300">
            <span className="text-sm text-gray-500">Avg. Credits to Degree</span>
            <span className="text-2xl font-semibold text-gray-800">
              {avgCredits}
            </span>
            <div className="text-sm text-red-600 flex items-center gap-1">
              <FaArrowDown />
              <span>8% from last year</span>
            </div>
          </div>

          {/* Card 4 */}
          <div className="bg-white rounded-lg p-4 flex flex-col border border-gray-300">
            <span className="text-sm text-gray-500">Avg. Cr. in Program Maps</span>
            <span className="text-2xl font-semibold text-gray-800">
              {avgCrInProgramMaps}
            </span>
            <div className="text-sm text-orange-500 flex items-center gap-1">
              <FaArrowUp />
              <span>7% from min cr. to graduate</span>
            </div>
          </div>

          {/* Card 5 */}
          <div className="bg-white rounded-lg p-4 flex flex-col border border-gray-300">
            <span className="text-sm text-gray-500">Credit Ratio</span>
            <span className="text-2xl font-semibold text-gray-800">
              {avgCreditRatio}%
            </span>
            <div className="text-sm text-green-600 flex items-center gap-1">
              <FaArrowUp />
              <span>5% from last year</span>
            </div>
          </div>
        </div>

        {/* TABLE */}
        <div className="relative overflow-x-auto">
          <table
            className="table-fixed w-full text-sm text-left text-gray-500"
            id="programs-table"
          >
            <thead className="text-xs text-gray-700 uppercase bg-gray-100">
              <tr>
                {/* Program Code */}
                <th
                  scope="col"
                  className="px-4 py-3 cursor-pointer w-36"
                  onClick={() => handleSortColumn('programCode')}
                >
                  <span className="flex items-center">
                    Program Code
                    <svg
                      className="w-4 h-4 ml-1"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="m8 15 4 4 4-4m0-6-4-4-4 4"
                      />
                    </svg>
                  </span>
                </th>

                {/* Program Title */}
                <th
                  scope="col"
                  className="px-4 py-3 cursor-pointer w-64"
                  onClick={() => handleSortColumn('programTitle')}
                >
                  <span className="flex items-center">
                    Program Title
                    <svg
                      className="w-4 h-4 ml-1"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="m8 15 4 4 4-4m0-6-4-4-4 4"
                      />
                    </svg>
                  </span>
                </th>

                {/* Years to Degree */}
                <th
                  scope="col"
                  className="px-4 py-3 cursor-pointer w-36"
                  onClick={() => handleSortColumn('yearsToDegree')}
                >
                  <span className="flex items-center">
                    Years to Degree
                    <svg
                      className="w-4 h-4 ml-1"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="m8 15 4 4 4-4m0-6-4-4-4 4"
                      />
                    </svg>
                  </span>
                </th>

                {/* Has Program Maps? */}
                <th
                  scope="col"
                  className="px-4 py-3 cursor-pointer w-44"
                  onClick={() => handleSortColumn('hasProgramMaps')}
                >
                  <span className="flex items-center">
                    Has Program Maps?
                    <svg
                      className="w-4 h-4 ml-1"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="m8 15 4 4 4-4m0-6-4-4-4 4"
                      />
                    </svg>
                  </span>
                </th>

                {/* Program Maps Count */}
                <th
                  scope="col"
                  className="px-4 py-3 cursor-pointer w-44"
                  onClick={() => handleSortColumn('programMapsCount')}
                >
                  <span className="flex items-center">
                    Program Maps Count
                    <svg
                      className="w-4 h-4 ml-1"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="m8 15 4 4 4-4m0-6-4-4-4 4"
                      />
                    </svg>
                  </span>
                </th>

                {/* Credits to Degree */}
                <th
                  scope="col"
                  className="px-4 py-3 cursor-pointer w-36"
                  onClick={() => handleSortColumn('creditsToDegree')}
                >
                  <span className="flex items-center">
                    Credits to Degree
                    <svg
                      className="w-4 h-4 ml-1"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="m8 15 4 4 4-4m0-6-4-4-4 4"
                      />
                    </svg>
                  </span>
                </th>

                {/* Credit Ratio */}
                <th
                  scope="col"
                  className="px-4 py-3 cursor-pointer w-36"
                  onClick={() => handleSortColumn('creditRatio')}
                >
                  <span className="flex items-center">
                    Credit Ratio
                    <svg
                      className="w-4 h-4 ml-1"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="m8 15 4 4 4-4m0-6-4-4-4 4"
                      />
                    </svg>
                  </span>
                </th>
              </tr>
            </thead>

            <tbody>
              {currentTableData.map((program, idx) => (
                <tr
                  key={idx}
                  className="bg-white border-b hover:!bg-gray-50 cursor-pointer"
                  onClick={() => handleRowClick(program)}
                >
                  {/* Program Code */}
                  <td className="px-4 py-3 font-medium text-gray-900">
                    {program.programCode}
                  </td>

                  {/* Program Title */}
                  <td className="px-4 py-3">{program.programTitle}</td>

                  {/* Years to Degree (horizontal bar) */}
                  <td className="px-4 py-3">
                    <YearsToDegreeBar years={program.yearsToDegree} />
                  </td>

                  {/* Has Program Maps? */}
                  <td className="px-4 py-3">
                    {program.hasProgramMaps ? 'Yes' : 'No'}
                  </td>

                  {/* Program Maps Count */}
                  <td className="px-4 py-3">
                    {program.programMapsCount ?? '—'}
                  </td>

                  {/* Credits to Degree */}
                  <td className="px-4 py-3">{program.creditsToDegree}</td>

                  {/* Credit Ratio (arrow up/down) */}
                  <td className="px-4 py-3">
                    <CreditRatioCell ratio={program.creditRatio} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* PAGINATION */}
        <nav
          className="flex items-center flex-col md:flex-row justify-between pt-4"
          aria-label="Programs table navigation"
        >
          {/* "Showing X-Y of Z" */}
          <span className="text-sm font-normal text-gray-500 dark:text-gray-400 mb-4 md:mb-0">
            Showing{' '}
            <span className="font-semibold text-gray-900 dark:text-white">
              {startIndex}-{endIndex}
            </span>{' '}
            of{' '}
            <span className="font-semibold text-gray-900 dark:text-white">
              {totalRecords}
            </span>
          </span>

          {/* Page numbers + Next/Prev */}
          <ul className="inline-flex -space-x-px text-sm h-8">
            {/* Previous */}
            <li>
              <button
                onClick={handlePreviousPage}
                disabled={currentPage === 1}
                className={`px-3 h-8 border border-gray-300 rounded-s-lg ${
                  currentPage === 1
                    ? 'text-gray-500 bg-white cursor-not-allowed'
                    : 'text-gray-500 bg-white hover:bg-gray-100 hover:text-gray-700'
                }`}
              >
                Previous
              </button>
            </li>

            {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => (
              <li key={pageNum}>
                <button
                  onClick={() => setCurrentPage(pageNum)}
                  className={`px-3 h-8 border border-gray-300 ${
                    pageNum === currentPage
                      ? 'text-blue-600 bg-blue-50'
                      : 'text-gray-500 bg-white hover:bg-gray-100 hover:text-gray-700'
                  }`}
                >
                  {pageNum}
                </button>
              </li>
            ))}

            {/* Next */}
            <li>
              <button
                onClick={handleNextPage}
                disabled={currentPage === totalPages || totalPages === 0}
                className={`px-3 h-8 border border-gray-300 rounded-e-lg ${
                  currentPage === totalPages || totalPages === 0
                    ? 'text-gray-500 bg-white cursor-not-allowed'
                    : 'text-gray-500 bg-white hover:bg-gray-100 hover:text-gray-700'
                }`}
              >
                Next
              </button>
            </li>
          </ul>
        </nav>
      </div>
    </div>
  );
};

export default Programs;
